/**
 * InternalMeetingDebriefView - Renders internal meeting debrief
 */

import React, { useState } from 'react';
import { Text, Flex, Icon } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import type { InternalMeetingDebrief } from 'App/resources/Model/MeetingDebrief';
import { parseDebrief, formatStatus, SummaryCard, ActionItemList, DiscussedSection } from './MeetingDebriefCommon';
import type { SummaryCardProps } from './MeetingDebriefCommon';
import './InternalMeetingDebriefView.scss';

interface InternalMeetingDebriefViewProps {
	debrief: InternalMeetingDebrief | string | null;
	t: (text: string) => string;
	hideTitle?: boolean;
	summaryHeaderEnd?: SummaryCardProps['headerEnd'];
}

export function InternalMeetingDebriefView({
	debrief: rawDebrief,
	t,
	hideTitle,
	summaryHeaderEnd
}: InternalMeetingDebriefViewProps) {
	const classes = new BemClass('MeetingDebriefView');
	const [isDiscussedExpanded, setIsDiscussedExpanded] = useState(true);
	const [isNextStepsExpanded, setIsNextStepsExpanded] = useState(true);

	const debrief = parseDebrief<InternalMeetingDebrief>(rawDebrief);
	const hasBlockers = debrief?.blockers && debrief.blockers.length > 0;
	const hasDiscussed = debrief?.discussed && debrief.discussed.length > 0;

	if (!debrief) {
		return (
			<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
				<Text>{t('meetingDebrief.noSummaryAvailable')}</Text>
			</Flex>
		);
	}

	return (
		<Flex direction="column" gap="u6" spacing="pl4 pr4 pb4 pt4" className={classes.elem('container').b()}>
			<SummaryCard
				summary={debrief.summary}
				classes={classes}
				t={t}
				hideTitle={hideTitle}
				headerEnd={summaryHeaderEnd}
			/>

			{hasDiscussed || hasBlockers ? (
				<Flex direction="column" gap="u4">
					<Flex
						justifyContent="space-between"
						alignItems="center"
						className={classes.elem('discussedHeader').b()}
						onClick={() => setIsDiscussedExpanded(!isDiscussedExpanded)}
					>
						<Flex gap="u2" alignItems="center">
							<Text size="lg" bold color="black">
								{t('meetingDebrief.whatWeDiscussed')}
							</Text>
							{debrief.date && (
								<Text size="sm" color="grey-11">
									{'• '}
									{new Date(debrief.date).toLocaleDateString('en-GB', {
										day: 'numeric',
										month: 'short',
										year: 'numeric'
									})}
								</Text>
							)}
						</Flex>
						<Icon
							name="chevron-down"
							className={classes
								.elem('discussedCaret')
								.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
								.b()}
						/>
					</Flex>
					<div
						className={classes
							.elem('discussedContent')
							.mod(isDiscussedExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('discussedSection').b()}>
							<Flex direction="column" gap="u3" spacing="p4">
								{hasDiscussed && <DiscussedSection topics={debrief.discussed!} classes={classes} />}

								{hasBlockers ? (
									<>
										{hasDiscussed && (
											<Flex justifyContent="center" alignItems="center" spacing="mt3">
												<Text size="lg" color="neutral-600" bold>
													{t('meetingDebrief.blockersRisks')}
												</Text>
											</Flex>
										)}
										<Flex direction="column" spacing={hasDiscussed ? 'pt4' : undefined}>
											<Flex direction="column" gap="u3">
												{debrief.blockers!.map((blocker, idx: number) => {
													const status = String(blocker.status ?? '').toLowerCase();
													const isResolved = status === 'resolved';

													return (
														<Flex
															key={`blocker-${idx}`}
															gap="u3"
															alignItems="center"
															justifyContent="space-between"
														>
															<Flex gap="u3" alignItems="center">
																<Text>
																	<Icon name="ban" color="danger-500" />
																</Text>
																<Flex direction="column" gap="u1">
																	<Text size="md">{blocker.issue}</Text>
																	<Text color="neutral-600" size="sm">
																		{isResolved
																			? formatStatus(blocker.status)
																			: blocker.status}
																	</Text>
																</Flex>
															</Flex>
														</Flex>
													);
												})}
											</Flex>
										</Flex>
									</>
								) : null}
							</Flex>
						</div>
					</div>
				</Flex>
			) : null}

			{debrief.actions && debrief.actions.length > 0 && (
				<Flex direction="column" gap="u3">
					<Flex
						alignItems="center"
						justifyContent="space-between"
						className={classes.elem('nextStepsHeader').b()}
						onClick={() => setIsNextStepsExpanded(!isNextStepsExpanded)}
					>
						<Text size="lg" bold>
							{t('meetingDebrief.nextSteps')}
						</Text>
						<Text size="lg">
							<Icon
								name="caret-down"
								color="neutral-600"
								className={classes
									.elem('nextStepsCaret')
									.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
									.b()}
							/>
						</Text>
					</Flex>
					<div
						className={classes
							.elem('nextStepsContent')
							.mod(isNextStepsExpanded ? 'expanded' : 'collapsed')
							.b()}
					>
						<div className={classes.elem('nextStepsSection').b()}>
							<ActionItemList actions={debrief.actions} classes={classes} />
						</div>
					</div>
				</Flex>
			)}
		</Flex>
	);
}
